import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from './Portfolio.module.scss';

const Portfolio = ({ folderPath, onFullscreenChange }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [autoScrollDirection, setAutoScrollDirection] = useState('right');
  const fullscreenImageRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const imageLoadedRef = useRef(false);
  const isTouchDevice = useRef(false);

  useEffect(() => {
    const loadPortfolioConfig = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL || ''}/portfolio/portfolio.json`);
        const data = await response.json();
        
        // Remove trailing slash if exists and split
        const pathParts = folderPath.replace(/\/$/, '').split('/');
        const projectName = pathParts.pop();
        
        // Find project key in a case-insensitive way
        const projectKey = data.projects ? Object.keys(data.projects).find(
          key => key.toLowerCase() === projectName.toLowerCase()
        ) : null;
        
        if (projectKey && data.projects[projectKey]) {
          const projectImages = data.projects[projectKey].images;
          const baseUrl = process.env.PUBLIC_URL || '';
          // Ensure folderPath starts with / if it doesn't
          const cleanFolderPath = folderPath.startsWith('/') ? folderPath : `/${folderPath}`;
          setImages(projectImages.map(image => `${baseUrl}${cleanFolderPath}/${image}`));
        } else {
          console.error(`Project ${projectName} not found in portfolio config`);
          setImages([]);
        }
      } catch (error) {
        console.error('Error loading portfolio config:', error);
        setImages([]);
      }
    };

    loadPortfolioConfig();
  }, [folderPath]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) {
      imageLoadedRef.current = false;
      stopAutoScroll();
    } else if (isFullscreen && imageLoadedRef.current) {
      const timer = setTimeout(() => {
        console.log('Initializing auto-scroll');
        startAutoScroll();
      }, 500);
      
      return () => {
        clearTimeout(timer);
        stopAutoScroll();
      };
    }
  }, [isFullscreen, imageLoadedRef.current]);

  const startAutoScroll = () => {
    console.log('Starting auto scroll');
    if (autoScrollIntervalRef.current) {
      console.log('Auto scroll already running');
      return;
    }

    const scrollSpeed = 2.25;
    
    autoScrollIntervalRef.current = setInterval(() => {
      if (!fullscreenImageRef.current || isDragging) {
        console.log('Skipping auto scroll:', isDragging ? 'dragging in progress' : 'no ref');
        return;
      }

      const container = fullscreenImageRef.current;
      const containerWidth = container.scrollWidth - container.clientWidth;
      const currentScroll = container.scrollLeft;

      console.log('Auto scroll state:', {
        direction: autoScrollDirection,
        currentScroll,
        containerWidth,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth
      });

      if (containerWidth <= 0) {
        console.log('No scrollable content, waiting for image to load properly');
        return;
      }

      if (autoScrollDirection === 'right') {
        if (currentScroll >= containerWidth - 1) {
          console.log('Changing direction to left');
          setAutoScrollDirection('left');
        } else {
          container.scrollLeft += scrollSpeed;
        }
      } else {
        if (currentScroll <= 1) {
          console.log('Changing direction to right');
          setAutoScrollDirection('right');
        } else {
          container.scrollLeft -= scrollSpeed;
        }
      }
    }, 16);
  };

  const stopAutoScroll = () => {
    console.log('Stopping auto scroll');
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const handleMouseDown = (e) => {
    console.log('Mouse down event');
    if (!fullscreenImageRef.current || isTouchDevice.current) return;
    stopAutoScroll();
    setIsDragging(true);
    setStartX(e.pageX - fullscreenImageRef.current.offsetLeft);
    setScrollLeft(fullscreenImageRef.current.scrollLeft);
  };

  const handleTouchStart = (e) => {
    console.log('Touch start event');
    if (!fullscreenImageRef.current) return;
    isTouchDevice.current = true;
    stopAutoScroll();
    setIsDragging(true);
    setStartX(e.touches[0].pageX - fullscreenImageRef.current.offsetLeft);
    setStartY(e.touches[0].pageY - fullscreenImageRef.current.offsetTop);
    setScrollLeft(fullscreenImageRef.current.scrollLeft);
    setScrollTop(fullscreenImageRef.current.scrollTop);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !fullscreenImageRef.current || isTouchDevice.current) return;
    e.preventDefault();
    const x = e.pageX - fullscreenImageRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    fullscreenImageRef.current.scrollLeft = scrollLeft - walk;
    console.log('Mouse move:', { x, walk, newScrollLeft: scrollLeft - walk });
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !fullscreenImageRef.current) return;
    const x = e.touches[0].pageX - fullscreenImageRef.current.offsetLeft;
    const y = e.touches[0].pageY - fullscreenImageRef.current.offsetTop;
    const walkX = (x - startX) * 2;
    const walkY = (y - startY) * 2;
    fullscreenImageRef.current.scrollLeft = scrollLeft - walkX;
    fullscreenImageRef.current.scrollTop = scrollTop - walkY;
    console.log('Touch move:', { x, y, walkX, walkY, newScrollLeft: scrollLeft - walkX, newScrollTop: scrollTop - walkY });
  };

  const handleDragEnd = () => {
    console.log('Drag end event');
    setIsDragging(false);
    setTimeout(() => {
      if (isFullscreen && imageLoadedRef.current) {
        startAutoScroll();
      }
    }, 1000);
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  };

  const toggleFullscreen = () => {
    const newFullscreenState = !isFullscreen;
    setIsFullscreen(newFullscreenState);
    if (onFullscreenChange) {
      onFullscreenChange(newFullscreenState);
    }
  };

  const getNextIndex = (current) => (current + 1) % images.length;
  const thumbnailIndexes = images.length > 1 ? [
    getNextIndex(currentIndex),
    images.length > 2 ? getNextIndex(getNextIndex(currentIndex)) : null
  ].filter(index => index !== null) : [];

  if (images.length === 0) {
    return <div className={styles.portfolio}>Loading portfolio...</div>;
  }

  return (
    <>
      <div className={styles.portfolio}>
        <div className={styles.portfolio__mainImage} onClick={toggleFullscreen}>
          <img src={images[currentIndex]} alt="Main" />
        </div>
        <div className={styles.portfolio__controls}>
          <button className={styles.portfolio__arrow} onClick={handlePrevious}>
            <ChevronLeft />
          </button>
          {thumbnailIndexes.map((index) => (
            <div
              key={index}
              className={styles.portfolio__thumbnail}
              onClick={() => setCurrentIndex(index)}
            >
              <img src={images[index]} alt={`Thumbnail ${index + 1}`} />
            </div>
          ))}
          <button className={styles.portfolio__arrow} onClick={handleNext}>
            <ChevronRight />
          </button>
        </div>
      </div>

      {isFullscreen && createPortal(
        <div 
          className={styles.fullscreen} 
          onClick={toggleFullscreen}
        >
          <button className={styles.fullscreen__close} onClick={toggleFullscreen}>
            <Close />
          </button>
          
          <div className={styles.fullscreen__content} onClick={(e) => e.stopPropagation()}>
            <button className={styles.fullscreen__nav} onClick={handlePrevious}>
              <ChevronLeft />
            </button>

            <div 
              ref={fullscreenImageRef}
              className={styles.fullscreen__imageContainer}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleDragEnd}
            >
              <img 
                src={images[currentIndex]} 
                alt="Fullscreen" 
                draggable="false"
                onLoad={() => {
                  console.log('Image loaded');
                  if (fullscreenImageRef.current) {
                    imageLoadedRef.current = true;
                    if (isFullscreen) {
                      startAutoScroll();
                    }
                  }
                }}
              />
            </div>

            <button className={styles.fullscreen__nav} onClick={handleNext}>
              <ChevronRight />
            </button>
          </div>

          <div className={styles.fullscreen__thumbnails} onClick={(e) => e.stopPropagation()}>
            {images.map((image, index) => (
              <div
                key={index}
                className={`${styles.fullscreen__thumbnail} ${index === currentIndex ? styles['fullscreen__thumbnail--active'] : ''}`}
                onClick={() => {
                  setCurrentIndex(index);
                  imageLoadedRef.current = false; // Reset for auto-scroll on new image
                }}
              >
                <img src={image} alt={`Thumb ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

Portfolio.propTypes = {
  folderPath: PropTypes.string.isRequired,
  onFullscreenChange: PropTypes.func
};

export default Portfolio; 